import { useCallback, useEffect, useMemo, useState } from 'react';
import type { VoidFunctionComponent } from 'react';

import { type OIDCProvider, OIDCProviderScope } from '../../../../../shared/model-typings/oidcprovider';
import { useDresser } from '../../../../contexts/DrawerContext';
import { useDeleteTrustRelationship, useTrustRelationships } from '../../../../hooks/api/oidc';
import SimpleReactTable from '../../../generic/SimpleReactTable';
import TableHeader from '../../../generic/TableHeader';
import SettingsPageTableActions from '../../../shared/settings/SettingsPageTableActions';
import { useCanCreateTrustRelationship, useCanManageTrustRelationship } from '../../shared/permissions';
import type { OIDCTrustRelationship } from '../../../../../shared/model-typings/oidctrustrelationship';

import ModalCreateTrustRelationship from './ModalCreateTrustRelationship';
import TrustRelationshipActionMenu from './TrustRelationshipActionMenu';
import TrustRelationshipDetailsDrawer from './TrustRelationshipDetailsDrawer';

type SortingRule = {
  id: string;
  desc: boolean;
};

const DEFAULT_SORT_MODE: SortingRule = { id: 'providerId', desc: false };

type TrustRelationshipsTableProps = {
  userId?: number;
  provider?: OIDCProvider;
  currentTeamId?: number;
  showCreateButton?: boolean;
};

const TrustRelationshipsTable: VoidFunctionComponent<TrustRelationshipsTableProps> = ({
  userId,
  provider,
  currentTeamId,
  showCreateButton = false,
}) => {
  const [sortMode, setSortMode] = useState<SortingRule>(DEFAULT_SORT_MODE);
  const [searchValue, setSearchValue] = useState<string>('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const { pushDrawer } = useDresser();
  const {
    data: pages,
    isLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useTrustRelationships({ userId, providerId: provider?.id });
  const deleteTrustRelationship = useDeleteTrustRelationship();

  // Determining whether the user can create a trust relationship is a bit tricky.
  // If we're viewing the trust relationships for a provider within the team settings, then the user must have
  // permission to create either org-scoped trust relationships OR team-scoped trust relationships for the current team.
  // If we're viewing trust relationships from the OIDC settings page, then we need to check what the scope of the
  // provider is. If it's a team-scoped provider, then the user must have either the org-scoped permission OR the
  // team-scoped permission for that team.
  // If the provider is organization-scoped, then the user can create a trust relationship if they have permission to
  // manage trust relationships at the organization level OR for ANY team, since the modal will allow them to select a
  // team.
  const canCreateTrustRelationshipForCurrentTeam = useCanManageTrustRelationship({ scopeId: currentTeamId });
  const canCreateTrustRelationshipForTeamScopedProvider = useCanManageTrustRelationship({ scopeId: provider?.scopeId });
  const canCreateGenericTrustRelationship = useCanCreateTrustRelationship();
  let canCreateTrustRelationship = false;
  if (currentTeamId) {
    canCreateTrustRelationship = canCreateTrustRelationshipForCurrentTeam;
  } else if (provider?.scope === OIDCProviderScope.TEAM) {
    canCreateTrustRelationship = canCreateTrustRelationshipForTeamScopedProvider;
  } else {
    canCreateTrustRelationship = canCreateGenericTrustRelationship;
  }

  // Auto-pagination
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage && !isLoading) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, isLoading, fetchNextPage]);

  const allTrustRelationships = useMemo(
    () => pages?.pages.flatMap((page) => page.results) ?? [],
    [pages],
  );

  const matchingTrustRelationships = useMemo(
    () => allTrustRelationships.filter((trustRelationship) =>
      trustRelationship.username?.toLowerCase().includes(searchValue.toLowerCase()) ||
      trustRelationship.teamName?.toLowerCase().includes(searchValue.toLowerCase()),
    ),
    [allTrustRelationships, searchValue],
  );

  const handleDelete = useCallback((id: number) => {
    deleteTrustRelationship.mutate(id);
  }, [deleteTrustRelationship]);

  const handleView = useCallback((id: number) => {
    const trustRelationship = allTrustRelationships.find((tr) => tr.id === id);
    if (trustRelationship) {
      pushDrawer(<TrustRelationshipDetailsDrawer trustRelationship={trustRelationship} />);
    }
  }, [allTrustRelationships, pushDrawer]);

  const ActionsCell = useCallback(
    // eslint-disable-next-line react/no-unused-prop-types
    ({ original }: { original: OIDCTrustRelationship }) => (
      <TrustRelationshipActionMenu
        trustRelationship={original}
        onDeleteTrustRelationship={handleDelete}
        onViewTrustRelationship={handleView}
      />
    ),
    [handleDelete, handleView],
  );

  const columns = useMemo(() => {
    const baseColumns = [];
    if (!provider) {
      baseColumns.push({
        id: 'providerId',
        Header: (
          <TableHeader title="Provider ID" sort={{ id: 'providerId', currentMode: sortMode }} />
        ),
        accessor: 'providerId',
        width: 100,
        resizable: false,
      }, {
        id: 'issuer',
        Header: (
          <TableHeader title="Issuer" sort={{ id: 'issuer', currentMode: sortMode }} />
        ),
        accessor: 'issuer',
        width: 300,
        resizable: true,
      });
    }
    if (!userId) {
      baseColumns.push({
        id: 'username',
        Header: (
          <TableHeader title="Service Account" sort={{ id: 'user', currentMode: sortMode }} />
        ),
        accessor: 'username',
        resizable: false,
      });
    }

    baseColumns.push(
      {
        id: 'scope',
        Header: (
          <TableHeader title="Scope" sort={{ id: 'scope', currentMode: sortMode }} />
        ),
        accessor: ({ scope }: OIDCTrustRelationship) =>
          scope === OIDCProviderScope.ORG ? 'Organization' : 'Team',
        width: 150,
        resizable: false,
      },
      {
        id: 'teamName',
        Header: (
          <TableHeader title="Team Name" sort={{ id: 'teamName', currentMode: sortMode }} />
        ),
        accessor: 'teamName',
        width: 200,
        resizable: true,
      },
      {
        id: 'actions',
        Header: <TableHeader title="Actions" />,
        resizable: false,
        width: 100,
        Cell: ActionsCell,
      },
    );

    return baseColumns;
  }, [provider, userId, sortMode, ActionsCell]);

  return (
    <>
      <SettingsPageTableActions>
        {showCreateButton && (
          <SettingsPageTableActions.TooltipButton
            id="add-trust-relationship-button"
            type="secondary"
            size="large"
            showTooltip={!canCreateTrustRelationship}
            tooltip="You are not permitted to add a trust relationship."
            onClick={() => setIsCreateModalOpen(true)}
            isDisabled={!canCreateTrustRelationship}
          >
            Add a trust relationship
          </SettingsPageTableActions.TooltipButton>
        )}
        <SettingsPageTableActions.SearchInput
          value={searchValue}
          onChange={setSearchValue}
          placeholder="Search by username or team name"
          label="search-by-username"
        />
      </SettingsPageTableActions>
      <SimpleReactTable
        isSortable
        data={matchingTrustRelationships}
        columns={columns}
        defaultSortMode={[DEFAULT_SORT_MODE]}
        onSortedChanged={setSortMode}
        showPagination
        cacheId="trustRelationships"
        isLoading={isLoading}
      />
      {provider && (
        <ModalCreateTrustRelationship
          isOpen={isCreateModalOpen}
          onHide={() => setIsCreateModalOpen(false)}
          providerId={provider.id}
          currentTeamId={currentTeamId}
        />
      )}
    </>
  );
};

export default TrustRelationshipsTable;
